import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
// Importing Material-UI components for styling and layout
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  CardActions,
  Divider,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

// Custom components and types
import { LocationConfiguration } from "./data/LocationConfiguration";
import { useParams } from "react-router-dom";
import { AlertsProps } from "components/Alerts/data/Alerts.type";

import {
  _getLocationImage,
  _uploadLocationImage,
} from "middlewares/Settings/configuration";
import LoadingIndicator from "../LoadingIndicator";
import SnackbarAlert from "../SnackbarAlert";

/**
 * Configuration component for managing location image upload.
 *
 * This component allows the user to upload an image for a location and displays the image preview.
 * It also provides feedback through snackbars and spinners.
 *
 * @component
 * @returns {JSX.Element} Configuration UI component.
 */
const Configuration: React.FC = () => {
  const { LocationId } = useParams();
  /**
   * Holds the current configuration state of the location.
   *
   * @type {[LocationConfiguration, Function]}
   */
  const [locationConfig, setLocationConfig] = useState<LocationConfiguration>({
    id: 0,
    locationID: Number(LocationId),
    imageName: "",
    base64Image: "",
  });
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [success, setSuccess] = useState<AlertsProps>({
    show: false,
    severity: "info",
    message: "",
  });

  /**
   * Handles the file drop event from the dropzone and starts base64 conversion.
   *
   * @param {File[]} acceptedFiles - Array of dropped files.
   */
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      convertToBase64(acceptedFiles[0]);
      setSelectedFile(acceptedFiles[0]);
    }
  }, []);

  /**
   * Converts a file to a base64 string and updates the location config state.
   *
   * @param {File} file - The image file to be converted.
   * @returns {void}
   */

  const convertToBase64 = (file: File) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setLocationConfig((prev) => ({
        ...prev,
        imageName: file.name,
        base64Image: reader.result as string,
      }));
      setIsSaveEnabled(true); // Enable Save button after upload
    };
    reader.onerror = (error) => console.error("Error converting file:", error);
  };

  /**
   * Handles the closing of the snackbar.
   *
   * @param {AlertsProps} state - The updated alert state.
   * @returns {void}
   */
  const handleCloseSnackBar = (state: AlertsProps) => {
    setSuccess(state);
  };

  /**
   * Handles the save action.
   * Currently a placeholder function that logs the config and shows an alert.
   *
   
   */

  const handleSave = async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("locationID", String(locationConfig.locationID));
      formData.append("imageName", locationConfig.imageName);
      formData.append("imageFile", selectedFile!, selectedFile!.name);
      formData.append("id", String(locationConfig.id));
      const res = await _uploadLocationImage(formData);
      if (res) {
        setLoading(false);

        const message = "Data Saved Successfully !";
        handleCloseSnackBar({
          show: true,
          severity: "success",
          message: message,
        });
      }
    } catch (err) {
      console.error("Error:", err);
      handleCloseSnackBar({
        show: true,
        severity: "error",
        message: "Failed to save data. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const getLocationImage = async () => {
    try {
      setLoading(true);
      const res = await _getLocationImage(Number(LocationId));
      if (res) {
        setLocationConfig((prev) => ({
          ...prev,
          id: res.id,
          locationID: res.locationID ?? Number(LocationId),
          imageName: res.imageName ?? "",
          base64Image: res.base64Image ?? "",
        }));
        setLoading(false);
      }
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getLocationImage();
  }, [LocationId]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { "image/*": [] }, // Accept only images
    maxSize: 10 * 1024 * 1024, // 10MB limit
    multiple: false, // Allow only one file at a time
  });

  return (
    <Box
      sx={{
        transform: 'scale(0.75)',
        transformOrigin: 'top left',
        width: '133.33%', // 1 / 0.75 for compensation
        minHeight: '100vh',
      }}
    >
      <Typography variant="h4" gutterBottom textAlign="center">
        Location Image
      </Typography>
      <Typography variant="body1" textAlign="center" sx={{ mb: 3 }}>
        Upload image to configurate your location
      </Typography>
      <Box sx={{ maxWidth: 800, margin: "auto", mt: 4 }}>
        <Card elevation={3}>
          <CardContent>
            {locationConfig.imageName && (
              <>
                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 2 }}>
                  <b>Selected File:</b> {locationConfig.imageName}
                </Typography>
                <Box
                  component="img"
                  src={locationConfig.base64Image}
                  alt="Uploaded preview"
                  sx={{
                    display: "block",
                    margin: "auto",
                    maxWidth: "100%",
                    maxHeight: 150,
                    borderRadius: 1,
                  }}
                />
                <Divider sx={{ my: 2 }} />
              </>
            )}

            <Box
              {...getRootProps()}
              sx={{
                border: "2px dashed #ccc",
                borderRadius: 2,
                padding: 3,
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: "#f9f9f9",
                "&:hover": { backgroundColor: "#f1f1f1" },
              }}
              data-testid="dropzone"
            >
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ fontSize: 40, color: "orange" }} />
              <Typography variant="body1" sx={{ mt: 1 }}>
                {locationConfig.imageName ?? "Drag and drop a file here"}
              </Typography>
              <Button
                variant="outlined"
                color="warning"
                sx={{ mt: 2, textTransform: "none" }}
              >
                Browse Image
              </Button>
              <Typography
                variant="caption"
                display="block"
                sx={{ mt: 1, color: "gray" }}
              >
                Maximum file size is 10 MB
              </Typography>
            </Box>
          </CardContent>
          <CardActions sx={{ justifyContent: "center" }}>
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
              disabled={!isSaveEnabled}
              data-testid="saveButton"
              onClick={handleSave}
            >
              Save
            </Button>
          </CardActions>
        </Card>
      </Box>
      <LoadingIndicator loading={loading} />
      <SnackbarAlert
        success={success}
        handleCloseSnackBar={handleCloseSnackBar}
      />
    </Box>
  );
};

export default Configuration;
